import * as React from 'react';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  black,
  blackText,
  blue,
  gray,
  grayPlaceholder,
  primarycolor
} from '../../constants/Colors';
import {
  formExtraNarrowMb,
  formLabelWrapper,
  formNarrowMb,
  formWideMb,
  titleMb
} from '../../constants/Location';

import type { OrderType } from '../../api/firebase/firestore/negotiation/negotiationType';
import type { DeclineCaseForm } from './DeclineModal';
import type { RegisterMediaCaseType } from './MediaRegisterCaseEditCard';
import type { MediaBasicRegisterType } from './MediaRegisterEditCard';
import type { MediaRegisterServiceType } from './MediaRegisterServiceEditCard';
import type { RegisterMediaUserType } from './MediaUserInfoEdit';
import type { ClientRegisterIssueType } from './RegisterIssueCard';
import type { ClientRegisterOrientationType } from './RegisterOrientationCard';
import type { SxProps } from '@mui/material';
import type {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge,
  UseFormRegister,
  UseFormWatch
} from 'react-hook-form';

type checkboxProps = React.ComponentProps<typeof Checkbox>;

interface List {
  value?: number;
  label?: string;
  description?: string;
  optionGuideText?: string;
  category?: string;
  subCategory?: string;
}

type Props = checkboxProps & {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  width?: string;
  list: List[];
  formMb?: string | undefined;
  guideText?: string;
  visible?: boolean;
  disabled?: boolean;
  secondLabel?: boolean;
  secondLabelText?: string;
  description?: string;
  name?: string;
  control?:
    | Control<ClientRegisterIssueType>
    | Control<ClientRegisterOrientationType>
    | Control<MediaBasicRegisterType>
    | Control<RegisterMediaUserType>
    | Control<MediaRegisterServiceType>
    | Control<RegisterMediaCaseType>
    | Control<OrderType>
    | Control<DeclineCaseForm>;
  checked?: boolean;
  consultationCheck?: boolean;
  register?: UseFormRegister<ClientRegisterIssueType>;
  registerOrientation?: UseFormRegister<ClientRegisterOrientationType>;
  registerMedia?: UseFormRegister<MediaBasicRegisterType>;
  registerMediaUser?: UseFormRegister<RegisterMediaUserType>;
  registerMediaService?: UseFormRegister<MediaRegisterServiceType>;
  registerMediaCase?: UseFormRegister<RegisterMediaCaseType>;
  registerOrder?: UseFormRegister<OrderType>;
  registerDecline?: UseFormRegister<DeclineCaseForm>;
  watch?: UseFormWatch<ClientRegisterIssueType>;
  watchOrientation?: UseFormWatch<ClientRegisterOrientationType>;
  watchMedia?: UseFormWatch<MediaBasicRegisterType>;
  watchMediaUser?: UseFormWatch<RegisterMediaUserType>;
  watchMediaService?: UseFormWatch<MediaRegisterServiceType>;
  watchMediaCase?: UseFormWatch<RegisterMediaCaseType>;
  watchOrder?: UseFormWatch<OrderType>;
  watchDecline?: UseFormWatch<DeclineCaseForm>;
};

const styles = {
  checkbox: {
    '&.Mui-disabled': {
      '.MuiTypography-root': {
        color: grayPlaceholder
      }
    },
    alignItems: 'flex-start',
    marginRight: 0
  },
  checkboxLabel: {
    color: blackText,
    marginTop: '10px'
  },
  description: {
    color: gray,
    fontSize: '12px',
    margin: '-11px 0 0 30px'
  },
  disabled: {
    opacity: 0.5
  },
  formLabelWrapper,
  guideCard: {
    '@media screen and (max-width:600px)': {
      width: '80%'
    },
    width: '480px'
  },
  guideCloseButton: {
    color: blue
  },
  guideMark: {
    color: gray,
    cursor: 'pointer',
    heihgt: '18px',
    marginLeft: '8px',
    verticalAlign: 'middle',
    width: '18px'
  },
  guideText: {
    color: blackText,
    fontSize: '14px',
    whiteSpace: 'pre-wrap'
  },
  guideTitle: {
    color: blackText,
    fontSize: '22px',
    marginBottom: titleMb,
    padding: 0
  },
  label: {
    fontSize: '12px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  }
};

const CustomCheckbox = React.forwardRef((props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const {
    labelText,
    requiredText,
    isRequired,
    error,
    helperText,
    list,
    formMb,
    guideText,
    visible = true,
    disabled,
    description,
    name,
    consultationCheck,
    register,
    registerOrientation,
    registerMedia,
    registerMediaUser,
    registerMediaService,
    registerMediaCase,
    registerOrder,
    registerDecline,
    watch,
    watchOrientation,
    watchMedia,
    watchMediaUser,
    watchMediaService,
    watchMediaCase,
    watchOrder,
    watchDecline
  } = props;

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  const inputformMb = (marginBottom: string | undefined): SxProps => {
    switch (marginBottom) {
      case 'narrow':
        return formNarrowMb;
      case 'extraNarrow':
        return formExtraNarrowMb;
      default:
        return formWideMb;
    }
  };

  // ヘルプマークの処理
  const [open, setOpen] = React.useState(false);
  const [itemGuideOpen, setItemGuideOpen] = React.useState(false);

  // 項目名横のヘルプアイコン
  const toggleOpen = (): void => {
    setOpen(!open);
  };

  // 選択肢横のヘルプアイコン
  const toggleItemGuideOpen = (): void => {
    setItemGuideOpen(!itemGuideOpen);
  };

  // どのIndexがチェックされているかを判定する関数
  const checkboxSwitcher = (
    index: number | undefined,
    category: string | undefined
  ): JSX.Element => {
    const issueCheckedFlug = (
      issueName:
        | 'kpi'
        | 'startDateCheck'
        | 'budgetCheck'
        | 'mediaTypeCheck'
        | 'mediaType'
        | 'menuCheck'
        | 'menu'
        | 'option',
      checkIndex: number
    ): boolean => {
      if (watch) {
        if (watch(`${issueName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const mediaCheckedFlug = (
      mediaName: 'genre' | 'funnel',
      checkIndex: number
    ): boolean => {
      if (watchMedia) {
        if (watchMedia(`${mediaName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const orienCheckedFlug = (
      orienName: 'article' | 'proposal' | 'attention',
      checkIndex: number
    ): boolean => {
      if (watchOrientation) {
        if (watchOrientation(`${orienName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const mediaUserCheckedFlug = (
      mediaUserName: 'target',
      checkIndex: number
    ): boolean => {
      if (watchMediaUser) {
        if (watchMediaUser(`${mediaUserName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const mediaServiceCheckedFlug = (
      mediaName: 'adType' | 'payment' | 'compensation' | 'guaranteeFlg',
      checkIndex: number
    ): boolean => {
      if (watchMediaService) {
        if (watchMediaService(`${mediaName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const mediaCaseCheckedFlug = (
      orienName: 'mainKpi',
      checkIndex: number
    ): boolean => {
      if (watchMediaCase) {
        if (watchMediaCase(`${orienName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const compensationOrderCheckedFlug = (
      orienName: 'compensation',
      checkIndex: number
    ): boolean => {
      if (watchOrder) {
        if (watchOrder(`${orienName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    const declineCaseCheckedFlug = (
      orienName: 'cause',
      checkIndex: number
    ): boolean => {
      if (watchDecline) {
        if (watchDecline(`${orienName}.${checkIndex}`)) {
          return true;
        }
        return false;
      }
      return false;
    };

    // 項目別にregisterの名称を分ける
    if (index) {
      if (name === 'kpi') {
        return (
          <Checkbox
            checked={issueCheckedFlug('kpi', index)}
            sx={category === 'other' ? { color: gray } : { color: black }}
            {...(register ? register(`kpi.${index}`) : '')}
          />
        );
      } else if (name === 'startDateCheck') {
        return (
          <Checkbox
            checked={issueCheckedFlug('startDateCheck', index)}
            {...(register ? register(`startDateCheck.${index}`) : '')}
          />
        );
      } else if (name === 'genre') {
        return (
          <Checkbox
            checked={mediaCheckedFlug('genre', index)}
            {...(registerMedia ? registerMedia(`genre.${index}`) : '')}
          />
        );
      } else if (name === 'funnel') {
        return (
          <Checkbox
            checked={mediaCheckedFlug('funnel', index)}
            {...(registerMedia ? registerMedia(`funnel.${index}`) : '')}
          />
        );
      } else if (name === 'budgetCheck') {
        return (
          <Checkbox
            checked={issueCheckedFlug('budgetCheck', index)}
            {...(register ? register(`budgetCheck.${index}`) : '')}
          />
        );
      } else if (name === 'mediaTypeCheck') {
        return (
          <Checkbox
            checked={issueCheckedFlug('mediaTypeCheck', index)}
            {...(register ? register(`mediaTypeCheck.${index}`) : '')}
          />
        );
      } else if (name === 'mediaType') {
        return (
          <Checkbox
            checked={issueCheckedFlug('mediaType', index)}
            {...(register ? register(`mediaType.${index}`) : '')}
          />
        );
      } else if (name === 'menuCheck') {
        return (
          <Checkbox
            checked={issueCheckedFlug('menuCheck', index)}
            {...(register ? register(`menuCheck.${index}`) : '')}
          />
        );
      } else if (name === 'menu') {
        return (
          <Checkbox
            checked={issueCheckedFlug('menu', index)}
            {...(register ? register(`menu.${index}`) : '')}
          />
        );
      } else if (name === 'option') {
        return (
          <Checkbox
            checked={issueCheckedFlug('option', index)}
            {...(register ? register(`option.${index}`) : '')}
          />
        );
      } else if (name === 'article') {
        return (
          <Checkbox
            checked={orienCheckedFlug('article', index)}
            {...(registerOrientation
              ? registerOrientation(`article.${index}`)
              : '')}
          />
        );
      } else if (name === 'proposal') {
        return (
          <Checkbox
            checked={orienCheckedFlug('proposal', index)}
            {...(registerOrientation
              ? registerOrientation(`proposal.${index}`)
              : '')}
          />
        );
      } else if (name === 'attention') {
        return (
          <Checkbox
            checked={orienCheckedFlug('attention', index)}
            {...(registerOrientation
              ? registerOrientation(`attention.${index}`)
              : '')}
          />
        );
      } else if (name === 'target') {
        return (
          <Checkbox
            checked={mediaUserCheckedFlug('target', index)}
            {...(registerMediaUser ? registerMediaUser(`target.${index}`) : '')}
          />
        );
      } else if (name === 'adType') {
        return (
          <Checkbox
            checked={mediaServiceCheckedFlug('adType', index)}
            {...(registerMediaService
              ? registerMediaService(`adType.${index}`)
              : '')}
          />
        );
      } else if (name === 'payment') {
        return (
          <Checkbox
            checked={mediaServiceCheckedFlug('payment', index)}
            {...(registerMediaService
              ? registerMediaService(`payment.${index}`)
              : '')}
          />
        );
      } else if (name === 'compensation') {
        return (
          <Checkbox
            checked={mediaServiceCheckedFlug('compensation', index)}
            {...(registerMediaService
              ? registerMediaService(`compensation.${index}`)
              : '')}
          />
        );
      } else if (name === 'guaranteeFlg') {
        return (
          <Checkbox
            checked={mediaServiceCheckedFlug('guaranteeFlg', index)}
            {...(registerMediaService
              ? registerMediaService(`guaranteeFlg.${index}`)
              : '')}
          />
        );
      } else if (name === 'mainKpi') {
        return (
          <Checkbox
            checked={mediaCaseCheckedFlug('mainKpi', index)}
            {...(registerMediaCase
              ? registerMediaCase(`mainKpi.${index}`)
              : '')}
          />
        );
      } else if (name === 'compensationOrder') {
        return (
          <Checkbox
            checked={compensationOrderCheckedFlug('compensation', index)}
            {...(registerOrder ? registerOrder(`compensation.${index}`) : '')}
          />
        );
      } else if (name === 'cause') {
        return (
          <Checkbox
            checked={declineCaseCheckedFlug('cause', index)}
            {...(registerDecline ? registerDecline(`cause.${index}`) : '')}
          />
        );
      }
      return <Checkbox />;
    }
    return <Checkbox />;
  };

  return (
    <>
      {visible && (
        <Typography sx={inputformMb(formMb)} align="center" component="div">
          <Typography align="left" component="div">
            <Typography
              align="left"
              sx={styles.formLabelWrapper}
              component="div"
            >
              <Typography color={gray} sx={styles.label} component="span">
                {labelText}
              </Typography>
              {isRequired ? (
                <Typography component="span" sx={styles.required}>
                  {requiredText}
                </Typography>
              ) : (
                ''
              )}
              {guideText ? (
                <Typography component="span" sx={{ marginTop: '10px' }}>
                  <HelpOutlineOutlinedIcon
                    sx={styles.guideMark}
                    onClick={toggleOpen}
                  />
                  <Dialog open={open} onClose={toggleOpen}>
                    <DialogContent sx={styles.guideCard}>
                      <DialogTitle sx={styles.guideTitle}>
                        {labelText}
                        {t('form.guideTitle')}
                      </DialogTitle>
                      <DialogContentText sx={styles.guideText}>
                        {guideText}
                      </DialogContentText>
                      <DialogActions>
                        <Button
                          onClick={toggleOpen}
                          sx={styles.guideCloseButton}
                        >
                          {t('form.guideClose')}
                        </Button>
                      </DialogActions>
                    </DialogContent>
                  </Dialog>
                </Typography>
              ) : (
                ''
              )}
              {description && <FormHelperText>{description}</FormHelperText>}
            </Typography>
            <FormHelperText error={error}>
              {error && textfieldHelper()}
            </FormHelperText>
            <FormGroup>
              {list.map((item, index) => (
                <Grid key={item.value}>
                  {item.category === 'recommend' && index === 0 ? (
                    <Typography
                      component="div"
                      sx={{ color: black, fontSize: '14px', marginTop: '8px' }}
                    >
                      {t('kpi.recommend')}
                    </Typography>
                  ) : item.category === 'other' && index === 3 ? (
                    <Typography
                      component="div"
                      sx={{ color: gray, fontSize: '14px', marginTop: '8px' }}
                    >
                      {t('kpi.other')}
                    </Typography>
                  ) : item.category === 'common' ? (
                    <Typography
                      component="div"
                      sx={{ color: black, fontSize: '14px', marginTop: '12px' }}
                    >
                      ＜{t('proposal.common')}＞
                    </Typography>
                  ) : item.category === 'web' &&
                    item.subCategory === 'article' ? (
                    <>
                      <Typography
                        component="div"
                        sx={{
                          color: black,
                          fontSize: '14px',
                          marginTop: '12px'
                        }}
                      >
                        ＜{t('mediaType.label9')}＞
                      </Typography>
                      <Typography
                        component="div"
                        sx={{ color: gray, fontSize: '12px' }}
                      >
                        {t('proposal.webArticle')}
                      </Typography>
                    </>
                  ) : item.subCategory === 'banner' ? (
                    <Typography
                      component="div"
                      sx={{ color: gray, fontSize: '12px', marginTop: '12px' }}
                    >
                      {t('proposal.webBanner')}
                    </Typography>
                  ) : item.category === 'ooh' ? (
                    <Typography
                      component="div"
                      sx={{ color: black, fontSize: '14px', marginTop: '12px' }}
                    >
                      ＜{t('mediaType.label10')}＞
                    </Typography>
                  ) : item.category === 'paper' ? (
                    <Typography
                      component="div"
                      sx={{ color: black, fontSize: '14px', marginTop: '12px' }}
                    >
                      ＜{t('mediaType.label7')}＞＜{t('mediaType.label8')}＞
                    </Typography>
                  ) : item.category === 'tvOrRadio' ? (
                    <Typography
                      component="div"
                      sx={{ color: black, fontSize: '14px', marginTop: '12px' }}
                    >
                      ＜{t('mediaType.label5')}＞＜{t('mediaType.label6')}＞
                    </Typography>
                  ) : item.category === 'otherMediaType' ? (
                    <Typography
                      component="div"
                      sx={{ color: black, fontSize: '14px', marginTop: '12px' }}
                    >
                      ＜{t('mediaType.label99')}＞
                    </Typography>
                  ) : (
                    ''
                  )}
                  <Typography
                    component="div"
                    key={item.value}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <Typography
                      component="div"
                      sx={{ alignItems: 'center', display: 'flex' }}
                    >
                      <FormControlLabel
                        control={checkboxSwitcher(
                          item.value ? item.value : undefined,
                          item.category ? item.category : undefined
                        )}
                        sx={styles.checkbox}
                        label={
                          <Typography
                            sx={[
                              styles.checkboxLabel,
                              item.category === 'other'
                                ? { color: gray }
                                : { color: black }
                            ]}
                          >
                            {item.label}
                          </Typography>
                        }
                        {...(disabled && { disabled })}
                      />
                      {item.optionGuideText ? (
                        <Typography component="div">
                          <HelpOutlineOutlinedIcon
                            sx={styles.guideMark}
                            onClick={toggleItemGuideOpen}
                          />
                          <Dialog
                            open={itemGuideOpen}
                            onClose={toggleItemGuideOpen}
                          >
                            <DialogContent sx={styles.guideCard}>
                              <DialogTitle sx={styles.guideTitle}>
                                {labelText}
                                {t('form.guideTitle')}
                              </DialogTitle>
                              <DialogContentText sx={styles.guideText}>
                                {item.optionGuideText}
                              </DialogContentText>
                              <DialogActions>
                                <Button
                                  onClick={toggleItemGuideOpen}
                                  sx={styles.guideCloseButton}
                                >
                                  {t('form.guideClose')}
                                </Button>
                              </DialogActions>
                            </DialogContent>
                          </Dialog>
                        </Typography>
                      ) : (
                        ''
                      )}
                    </Typography>
                    <Typography
                      component="span"
                      sx={[
                        { ...(item.description ? styles.description : '') },
                        { ...(disabled ? styles.disabled : '') }
                      ]}
                    >
                      {item.description}
                    </Typography>
                  </Typography>
                </Grid>
              ))}
            </FormGroup>
            {consultationCheck && <Divider />}
          </Typography>
        </Typography>
      )}
    </>
  );
});
CustomCheckbox.displayName = 'CustomCheckbox';
export default CustomCheckbox;
